import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Select from "../forms/Select/Select";
import { RichText } from "../KaTeXComponent/RichText";
import { getQuestionByUUID, getQuestions } from "../../services/questionService";
import { isCanceledError } from "../../utils/examError";
import { focusRing, iconButtonClass, linkButtonClass, questionMeta, Spinner, StatusBadge } from "../../pages/Exams/TestBuilder/ui";
import PassageChip from "./PassageChip";
import useBankLists from "./useBankLists";
import { folderFilterGroups, folderPath } from "../../utils/folders";

const PAGE_SIZE = 20;
// The server's limit of questions in one passage.
const MAX_QUESTIONS = 200;
const listOf = (res) => (Array.isArray(res) ? res : res?.results || []);
const subjectUuidOf = (subject) => (typeof subject === "string" ? subject : subject?.uuid) || "";
// A row's topic and subtopic ("Topic › Subtopic"): its place inside the passage's own subject folder.
const topicOf = (row) => [row?.subtopic?.topic?.name, row?.subtopic?.name].filter(Boolean).join(" › ");
// The rows' icon buttons (preview, ↑ ↓ ✕): 44px on phones.
const rowButton = "max-md:h-11 max-md:w-11";

// The body of one bank question, fetched when its preview is opened.
function QuestionBody({ uuid }) {
  const [t] = useTranslation();
  const [state, setState] = useState({ data: null, error: null });
  useEffect(() => {
    const controller = new AbortController();
    getQuestionByUUID({ uuid, signal: controller.signal })
      .then((data) => setState({ data, error: null }))
      .catch((err) => !isCanceledError(err) && setState({ data: null, error: err }));
    return () => controller.abort();
  }, [uuid]);
  if (state.error) return <p className="text-xs text-red-600">{t("passages.picker.load_failed")}</p>;
  if (!state.data) return <Spinner />;
  return (
    <div className="space-y-2 text-sm text-slate-800 [&_img]:max-h-40 [&_img]:w-auto">
      <RichText html={state.data.body} />
      {state.data.answer_keys?.length > 0 && (
        <ul className="space-y-1">
          {state.data.answer_keys.map((option, i) => (
            <li key={option.uuid || i} className="flex gap-2">
              <span className={`font-semibold ${option.is_correct ? "text-emerald-700" : "text-slate-500"}`}>{option.option})</span>
              <div className="min-w-0">
                <RichText html={option.body} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Picks bank questions for a passage, in order. Left: the bank (search, subject, folder; questions of
 * another passage can't be picked); a row's checkbox and text both tick it. Right: the picked questions in passage
 * order, with ↑ ↓ ✕. Phones stack the two.
 * `lockedIds` (questions already linked, in the builder flow) stay first and can't be moved or removed.
 * `extraRows` are listed first: questions still linked to this passage on the server but taken out of the
 * selection (the bank list leaves linked questions out, so they could not be ticked again).
 * `selected` is a list of question rows ({uuid, code, name, status, ...}); `onChange(nextSelected)`.
 * `folder`: the passage's folder (a subject's uuid, or "null" for a passage without one). A passage's questions
 * live in its folder, so only that folder's questions are offered (inside a subject folder without the subject
 * filter, which could only empty the list, and with each row's topic instead of its whole path); without the prop
 * any folder can be picked.
 * `canAdd` false: no question can join the passage (an active BTS booklet prints it): only the picked ones are shown.
 */
export default function QuestionPicker({
  selected,
  onChange,
  lockedIds,
  passageUuid = null,
  subject = null,
  folder: passageFolder,
  inTest,
  extraRows = [],
  canAdd = true,
}) {
  const [t] = useTranslation();
  const { subjects, folders } = useBankLists();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [subjectFilter, setSubjectFilter] = useState(() => subjectUuidOf(subject));
  const [folder, setFolder] = useState("");
  const fixedFolder = passageFolder === undefined ? null : passageFolder || "null";
  const inSubjectFolder = !!fixedFolder && fixedFolder !== "null";
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ rows: [], count: 0, loading: true, error: null });
  const [attempt, setAttempt] = useState(0);
  const [previewId, setPreviewId] = useState(null);

  const selectedIds = useMemo(() => new Set(selected.map((q) => q.uuid)), [selected]);
  const locked = lockedIds || new Set();

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => setPage(1), [search, subjectFilter, folder, fixedFolder]);

  useEffect(() => {
    // Nothing can be added: the bank is not shown.
    if (!canAdd) return undefined;
    const controller = new AbortController();
    setResult((prev) => ({ ...prev, loading: true, error: null, ...(page === 1 ? { rows: [] } : {}) }));
    getQuestions({
      params: {
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
        // A subject folder holds its subject's questions only: no subject filter there (one left from another folder
        // the passage was in would empty the list).
        subject: (!inSubjectFolder && subjectFilter) || undefined,
        folder: fixedFolder || folder || undefined,
        has_passage: "false",
        ordering: "-created",
      },
      signal: controller.signal,
    })
      .then((data) => {
        const rows = listOf(data);
        setResult((prev) => ({
          rows: page === 1 ? rows : [...prev.rows, ...rows.filter((r) => !prev.rows.some((p) => p.uuid === r.uuid))],
          count: typeof data?.count === "number" ? data.count : rows.length,
          loading: false,
          error: null,
        }));
      })
      .catch((err) => {
        if (!isCanceledError(err)) setResult((prev) => ({ ...prev, loading: false, error: err }));
      });
    return () => controller.abort();
  }, [canAdd, page, search, subjectFilter, folder, fixedFolder, inSubjectFolder, attempt]);

  const subjectOptions = useMemo(
    () => [{ value: "", label: t("questions_page.filters.all_subjects") }, ...subjects.map((s) => ({ value: s.uuid, label: s.name }))],
    [subjects, t]
  );
  const folderOptions = useMemo(
    () => [{ value: "", label: t("passages.picker.folder_all") }, ...folderFilterGroups(folders, t("folders.all_subjects"))],
    [folders, t]
  );
  const showSubject = !inSubjectFolder;
  const showFolder = folders.length > 0 && !fixedFolder;
  const filterColumns =
    showSubject && showFolder
      ? "sm:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.8fr)]"
      : showSubject || showFolder
        ? "sm:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)]"
        : "";

  const toggle = (row) => {
    if (selectedIds.has(row.uuid)) {
      if (!locked.has(row.uuid)) onChange(selected.filter((q) => q.uuid !== row.uuid));
    } else if (selected.length < MAX_QUESTIONS) {
      onChange([...selected, row]);
    }
  };
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= selected.length || locked.has(selected[target].uuid)) return;
    const next = [...selected];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className={canAdd ? "grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]" : ""}>
      {canAdd && (
        <section aria-label={t("passages.picker.available")} className="min-w-0 space-y-3">
          <div className={`grid gap-2 ${filterColumns}`}>
            <label className="relative block">
              <span className="sr-only">{t("passages.picker.search")}</span>
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("passages.picker.search")}
                className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15"
              />
            </label>
            {showSubject && (
              <Select
                options={subjectOptions}
                value={subjectFilter}
                onChange={(o) => setSubjectFilter(o?.value || "")}
                placeholder={t("passages.editor.subject")}
                isSearchable
              />
            )}
            {showFolder && (
              <Select
                options={folderOptions}
                value={folder}
                onChange={(o) => setFolder(o?.value || "")}
                isSearchable
              />
            )}
          </div>
          {fixedFolder && (
            <p className="text-xs font-medium text-slate-700">
              {fixedFolder === "null"
                ? t("passages.picker.folder_fixed_none")
                : t("passages.picker.folder_fixed", {
                    name: folderPath(folders.find((f) => f.uuid === fixedFolder), new Map(folders.map((f) => [f.uuid, f]))) || "…",
                  })}
            </p>
          )}
          <p className="text-xs text-slate-500">{t("passages.picker.only_free_hint")}</p>
          <ul className="max-h-[22rem] divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200">
            {[...extraRows, ...result.rows.filter((row) => !extraRows.some((extra) => extra.uuid === row.uuid))].map((row) => {
              const otherPassage = row.passage?.uuid && row.passage.uuid !== passageUuid ? row.passage : null;
              const checked = selectedIds.has(row.uuid);
              // A draft can't be linked; one already in this passage (offered again as an extra row) can stay.
              const draftBlocked = !checked && row.status?.code === "DRAFT" && !extraRows.some((extra) => extra.uuid === row.uuid);
              const full = !checked && selected.length >= MAX_QUESTIONS;
              const disabled = !!otherPassage || draftBlocked || full || (checked && locked.has(row.uuid));
              const meta = inSubjectFolder ? topicOf(row) : questionMeta(row);
              return (
                <li key={row.uuid} className="px-3 py-2">
                  <div className="flex items-start gap-2">
                    {/* The whole text ticks the row too: a bigger target than the box alone. */}
                    <label className={`flex min-w-0 flex-1 items-start gap-3 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggle(row)}
                        aria-label={t("passages.picker.select", { code: row.code || row.name })}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-600 disabled:opacity-40"
                      />
                      <span className="block min-w-0 flex-1">
                        <span className={`block break-words text-sm ${disabled && !checked ? "text-slate-400" : "text-slate-900"}`}>
                          {row.name || row.code}
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-1.5">
                          {row.code && <span className="font-mono text-xs text-slate-500">{row.code}</span>}
                          <StatusBadge status={row.status} />
                          {inTest?.has(row.uuid) && (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                              {t("passages.picker.in_test")}
                            </span>
                          )}
                          {otherPassage && <PassageChip passage={otherPassage} />}
                          {meta && <span className="truncate text-xs text-slate-500">{meta}</span>}
                        </span>
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setPreviewId((id) => (id === row.uuid ? null : row.uuid))}
                      aria-expanded={previewId === row.uuid}
                      aria-label={t("test_bank.builder.view")}
                      title={t("test_bank.builder.view")}
                      className={`${iconButtonClass} h-8 w-8 ${rowButton}`}
                    >
                      <EyeIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  {previewId === row.uuid && (
                    <div className="ml-7 mt-2 rounded-lg bg-slate-50 p-3">
                      <QuestionBody uuid={row.uuid} />
                    </div>
                  )}
                </li>
              );
            })}
            {!result.loading && !result.error && result.rows.length === 0 && extraRows.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-slate-500">{t("passages.picker.empty")}</li>
            )}
            {result.loading && (
              <li className="flex justify-center px-3 py-4">
                <Spinner />
              </li>
            )}
            {result.error && (
              <li role="alert" className="flex flex-wrap items-center justify-center gap-2 px-3 py-4 text-sm text-red-700">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t("passages.picker.load_failed")}
                <button type="button" onClick={() => setAttempt((n) => n + 1)} className={linkButtonClass}>
                  {t("passages.panel.retry")}
                </button>
              </li>
            )}
          </ul>
          {!result.loading && !result.error && result.rows.length < result.count && (
            <button type="button" onClick={() => setPage((p) => p + 1)} className={`${linkButtonClass} max-md:min-h-[2.75rem]`}>
              {t("passages.picker.load_more")}
            </button>
          )}
        </section>
      )}

      <section aria-label={t("passages.picker.selected", { count: selected.length })} className="min-w-0 space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="text-sm font-semibold text-slate-900">{t("passages.picker.selected", { count: selected.length })}</h4>
        </div>
        <p className="text-xs text-slate-500">{t("passages.picker.selected_hint")}</p>
        {selected.length >= MAX_QUESTIONS && (
          <p className="text-xs font-medium text-amber-700">{t("passages.picker.limit", { max: MAX_QUESTIONS })}</p>
        )}
        {selected.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-6 text-center text-sm text-slate-500">
            {t("passages.picker.selected_empty")}
          </p>
        ) : (
          <ol className="space-y-1.5">
            {selected.map((q, i) => {
              const isLocked = locked.has(q.uuid);
              return (
                <li key={q.uuid} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                  <span className="w-6 shrink-0 text-right text-sm font-semibold tabular-nums text-indigo-700">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-900">{q.name || q.code}</p>
                    {q.code && <p className="truncate font-mono text-xs text-slate-500">{q.code}</p>}
                  </div>
                  {isLocked ? (
                    <LockClosedIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                  ) : (
                    <div className="flex shrink-0 items-center">
                      <button
                        type="button"
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || locked.has(selected[i - 1]?.uuid)}
                        aria-label={`${t("passages.picker.move_up")}: ${q.code || q.name}`}
                        className={`${iconButtonClass} h-8 w-8 ${rowButton}`}
                      >
                        <ArrowUpIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(i, 1)}
                        disabled={i === selected.length - 1}
                        aria-label={`${t("passages.picker.move_down")}: ${q.code || q.name}`}
                        className={`${iconButtonClass} h-8 w-8 ${rowButton}`}
                      >
                        <ArrowDownIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onChange(selected.filter((s) => s.uuid !== q.uuid))}
                        aria-label={`${t("passages.picker.remove")}: ${q.code || q.name}`}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition duration-200 hover:bg-red-50 hover:text-red-600 ${rowButton} ${focusRing}`}
                      >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
